import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck, HardHat, BadgeCheck, CalendarCheck, XCircle, CheckCircle2, Users, MapPin } from 'lucide-react';
import { alertasApi, errorMsg } from '../alertas/alertasObra.api';
import { COMBO, SEGURIDAD, estadoCombo, estadoForzado, estadoSeguridad, olvidarCombo, olvidarSeguridad, seguridadForzada } from './comboIA';
import ComboIAVenta from './ComboIAVenta';
import { normalizar } from './ComboIAGate';

/* Puerta del adicional "Seguridad e Higiene" (USD 10/mes por cámara, sobre IA Pro).
   - Sin IA Pro: la pantalla de activación de IA Pro (explica que Seguridad se suma después).
   - Con IA Pro y sin el adicional: pantalla de activación del adicional (30 días gratis, sin tarjeta, con consentimiento).
   - En prueba: banda con los días que quedan.
   Lisual (admin) siempre ve la página; si el cliente no lo tiene, con una banda gris (y no se verifica nada: no se gasta).
   Si el estado no se puede leer (backend caído), se muestra la página: nunca se le corta a quien paga. */

const CONSENTIMIENTO = 'Acepto que Lisual analice con inteligencia artificial a las personas que aparecen en las fotos de esta cámara (uso de casco y chaleco, presencia en zonas de riesgo) a partir de este momento, durante 30 días gratis y sin tarjeta. No se reconocen caras.';

const Banda = ({ dias }) => (
  <div className="mb-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-seg-banda="prueba">
    <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
    <span>
      <b>Seguridad e Higiene en prueba:</b> {dias === 0 ? 'hoy es el último día' : dias === 1 ? 'te queda 1 día' : `te quedan ${dias ?? SEGURIDAD.diasPrueba} días`} gratis.{' '}
      <span className="text-emerald-800/80">Después, USD {SEGURIDAD.precioUsd} por mes por cámara. Cancelás cuando quieras.</span>
    </span>
  </div>
);

const BandaAdmin = () => (
  <div className="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600" data-seg-banda="admin">
    <ShieldCheck size={14} className="shrink-0" />
    Vista de Lisual: este cliente no tiene el adicional Seguridad e Higiene en esta cámara (él ve la pantalla de activación). No se verifican fotos nuevas.
  </div>
);

const QUE_TRAE = [
  [BadgeCheck, 'Datos verificados', 'Cada persona se revisa una por una con IA. Solo mostramos lo que se ve con claridad.'],
  [HardHat, 'Casco y chaleco', 'Uso por día y por hora, con la meta que vos definas, y los recortes de cada falta.'],
  [Users, 'Personas en obra', 'Cuánta gente hubo, el pico del día y las horas-persona.'],
  [MapPin, 'Zonas de riesgo', 'Marcás los lugares peligrosos y ves quién pasa por ahí y con qué protección.'],
];

function SeguridadVenta({ feedID, estado, onActivado, onSimularAlta }) {
  const [acepto, setAcepto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const dias = estado?.trialDays || SEGURIDAD.diasPrueba;
  const precio = estado?.priceUsd || SEGURIDAD.precioUsd;

  const activar = async () => {
    if (!acepto) return;
    setAviso(null);
    if (estado?.simulado) {
      setAviso({ tipo: 'info', txt: 'Vista previa local: no se activa nada. Así se ve con el adicional activo.' });
      setTimeout(() => onSimularAlta && onSimularAlta(), 900);
      return;
    }
    setOcupado(true);
    try {
      const r = await alertasApi.seguridadActivar(feedID, 'Seguridad e Higiene');
      olvidarSeguridad(feedID);
      onActivado(r);
    } catch (e) {
      setAviso({ tipo: 'error', txt: errorMsg(e) });
      setOcupado(false);
    }
  };

  return (
    <div className="space-y-4" data-seg-venta>
      <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-emerald-900 text-white p-5 sm:p-7 overflow-hidden relative">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full px-2.5 py-1">
          <ShieldCheck size={13} />Adicional de {COMBO.nombre}
        </span>
        <h3 className="mt-3 text-xl sm:text-2xl font-bold leading-tight max-w-xl">Sumá Seguridad e Higiene a esta cámara</h3>
        <p className="mt-2 text-sm text-gray-300 max-w-xl">
          La IA revisa a cada persona de tus fotos y te dice quién usa casco y chaleco, cuánta gente hubo en obra y quién pasó por las zonas de riesgo.
        </p>
        {estado?.pruebaUsada ? (
          <p className="mt-5 text-sm rounded-xl bg-white/10 px-4 py-3 max-w-xl" data-seg-usada>
            Esta cámara ya usó sus {dias} días gratis de Seguridad e Higiene. Escribinos y lo reactivamos.
          </p>
        ) : (
          <div className="mt-5 space-y-3 max-w-xl">
            <div className="text-sm text-emerald-200 font-semibold">{dias} días gratis · sin tarjeta · después USD {precio} por mes por cámara</div>
            <label className="flex items-start gap-2 text-sm text-gray-100 cursor-pointer">
              <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-400" data-seg-acepto />
              <span>{estado?.consentimiento || CONSENTIMIENTO}</span>
            </label>
            <button type="button" onClick={activar} disabled={ocupado || !acepto} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-gray-900 font-semibold px-5 py-3 text-sm disabled:opacity-50 transition-colors" data-seg-activar>
              {ocupado ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}Activar Seguridad e Higiene gratis
            </button>
          </div>
        )}
        {aviso && <p className={`mt-3 text-xs rounded-lg px-3 py-2 ${aviso.tipo === 'error' ? 'bg-red-500/15 text-red-200' : 'bg-white/10 text-gray-200'}`}>{aviso.txt}</p>}
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[[CalendarCheck, `${dias} días gratis para probarlo con tu obra`], [XCircle, 'Sin tarjeta y sin compromiso'], [CheckCircle2, 'Las personas se analizan desde que lo activás']].map(([Icon, t]) => (
          <li key={t} className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-xl px-3 py-2.5"><Icon size={15} className="text-emerald-600 shrink-0" />{t}</li>
        ))}
      </ul>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {QUE_TRAE.map(([Icon, t, x]) => (
          <div key={t} className="rounded-2xl border border-gray-200 bg-white p-4 flex gap-3">
            <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0"><Icon size={18} /></span>
            <div><b className="text-sm text-gray-800">{t}</b><p className="text-xs text-gray-600 mt-1 leading-relaxed">{x}</p></div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 px-1">No reconocemos caras. Calzado y guantes no se miden: desde una cámara de obra no se ven con la claridad suficiente.</p>
    </div>
  );
}

const SeguridadGate = ({ feedID, children }) => {
  const forzada = seguridadForzada();
  const forzadoPro = estadoForzado();
  const [estado, setEstado] = useState(forzada);
  const [pro, setPro] = useState(forzadoPro);
  const [error, setError] = useState(false);

  const cargar = useCallback((refrescar) => {
    if (forzada || !feedID) return;
    setError(false);
    estadoSeguridad(feedID, { refrescar }).then(setEstado).catch(() => setError(true));
  }, [feedID, forzada]);
  useEffect(() => { cargar(false); }, [cargar]);
  // Si hace falta IA Pro, su estado completo (para la pantalla de activación de IA Pro)
  useEffect(() => {
    if (!estado?.requierePro || forzadoPro || !feedID) return;
    estadoCombo(feedID).then((r) => setPro(normalizar(r))).catch(() => {});
  }, [estado, feedID, forzadoPro]);

  if ((!feedID && !forzada) || error) return children;
  if (!estado) return <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 size={16} className="animate-spin" />Cargando…</div>;

  if (estado.estado === 'no') {
    if (estado.admin) return <><BandaAdmin />{children}</>;
    if (estado.requierePro) {
      return (
        <ComboIAVenta
          feedID={feedID}
          pagina="Seguridad e Higiene"
          estado={pro || { simulado: !!forzada }}
          onSimularAlta={() => setEstado({ ...estado, requierePro: false })}
          onActivado={() => { olvidarCombo(feedID); olvidarSeguridad(feedID); cargar(true); }}
        />
      );
    }
    return <SeguridadVenta feedID={feedID} estado={estado} onSimularAlta={() => setEstado({ ...estado, estado: 'prueba', diasRestantes: SEGURIDAD.diasPrueba })} onActivado={(r) => setEstado(r)} />;
  }
  return (
    <>
      {estado.estado === 'prueba' && <Banda dias={estado.diasRestantes} />}
      {children}
    </>
  );
};

export default SeguridadGate;
